import { useMemo } from "react";
import type { View } from "@common/types/views/views";
import { ViewDensity } from "@common/types/views/views";
import type { ViewConfig } from "@lib/views";
import { ViewTable } from "@design-system/ViewTable/ViewTable";
import previewStyles from "../DatePickerPlayground.module.scss";
import tableStyles from "../TablesPlayground.module.scss";
import { moveFlatRow } from "./viewTablePlaygroundDnd";
import {
    type ReorderRow,
    createDragHandleCell,
    createNameCell,
    createOwnerCell,
    createReorderRows,
} from "./viewTablePlaygroundShared";

export interface RowReorderingState {
    view: View;
    rows: ReorderRow[];
}

export interface RowReorderingScenarioProps {
    density: ViewDensity;
    state: RowReorderingState;
    onRowsChange: (rows: ReorderRow[]) => void;
}

export const createRowReorderingState = (): RowReorderingState => ({
    view: {
        columns: [{ id: "drag" }, { id: "name" }, { id: "owner" }],
        filters: {},
        options: {
            density: ViewDensity.MD,
        },
    },
    rows: createReorderRows(),
});

const DragHandleCell = createDragHandleCell<ReorderRow>();
const NameCell = createNameCell<ReorderRow>();
const OwnerCell = createOwnerCell<ReorderRow>();

const rowReorderingConfig: ViewConfig<
    ReorderRow,
    "drag" | "name" | "owner",
    never
> = {
    columns: {
        drag: {
            accessorKey: "name",
            name: "Drag",
            header: "",
            cell: DragHandleCell,
            minSize: 48,
            maxSize: 48,
        },
        name: {
            accessorKey: "name",
            name: "Name",
            header: "Name",
            cell: NameCell,
            minSize: 240,
        },
        owner: {
            accessorKey: "owner",
            name: "Owner",
            header: "Owner",
            cell: OwnerCell,
            minSize: 180,
        },
    },
};

export const RowReorderingScenario = ({
    density,
    state,
    onRowsChange,
}: RowReorderingScenarioProps) => {
    const view = useMemo<View>(
        () => ({
            ...state.view,
            options: {
                ...state.view.options,
                density,
            },
        }),
        [state.view, density],
    );

    return (
        <>
            <div className={tableStyles.tableFrame}>
                <ViewTable
                    data={state.rows}
                    view={view}
                    config={rowReorderingConfig}
                    enableColumnResizing={false}
                    rowDragAndDrop={{
                        canDragRow: () => true,
                        dragHandleColumnId: "drag",
                        onRowDrop: ({ activeRow, overRow, dropPosition }) => {
                            onRowsChange(
                                moveFlatRow({
                                    rows: state.rows,
                                    activeId: activeRow.id,
                                    overId: overRow.id,
                                    dropPosition,
                                }),
                            );
                        },
                    }}
                    outerBorder
                    style={{ height: "320px" }}
                />
            </div>
            <div className={previewStyles.output}>
                <span>Order</span>
                <div role="status" aria-live="polite">
                    <ol>
                        {state.rows.map((row) => (
                            <li key={row.id}>{row.name}</li>
                        ))}
                    </ol>
                </div>
            </div>
        </>
    );
};
